import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';
import { ordersApi, supportingDataApi } from '@/services/api';
import { useSchemeClients, clientIdsForScheme, pruneClientIds } from '@/hooks/useSchemeClients';
import { getSelectedGroupValue as getSchemeGroupValue, isGlobalSelection } from '@/utils/orderSchemeGroup';
import OrderSchemeSelector from '@/components/order_management/OrderSchemeSelector';
import OrderClientSelector from '@/components/order_management/OrderClientSelector';
import FormCard, {
  FormSection,
  FormGrid,
  FormField,
  FormInput,
  FormSelect,
  FormReactSelect,
  FormFieldNote,
  SelectOption,
} from '@/components/utils/FormCard';
import FeatureFlag from '@/components/utils/FeatureFlag';
import { Scheme, Company, ORDER_CALC_TYPE_MUTUAL_FUND } from '@/types/client';
import { FEATURE_FLAGS } from '@/config/featureFlagKeys';
import { formatCurrency as formatBaseCurrency, CurrencyCode } from '@/utils/formatters';
import { proportionalSellPct, sellValueAtPct, splitRedemptionAmount, usableRedemptionCash } from '@/utils/partialRedemption';
import formDefaults from '@/config/formDefaults.json';
import shared from '@/components/utils/shared.module.css';
import { Button, Placeholder, StatGrid, StatItem } from '@/components/ui';

interface SellOrderFormData {
  scheme_ids: number[];
  company_id: number;
  order_type: number | string;
  h1: number | string;
  h2: number | string;
  median_volume_pct: number | string;
  apply_volume_cap: boolean;
  condition_1: number | string;
  condition_2: number | string;
  /** Null until the user types one; the region's default applies until then. */
  min_order_val: number | string | null;
  sell_method: number | string;
  is_only_long_term: boolean;
  long_term_months: number | string;
  is_redemption: boolean;
  is_full_redemption: boolean;
  client_ids: number[];
}

type HoldingRow = {
  company_id: number;
  company_symbol: string;
  unit_price: number;
  company_name: string;
  is_inherited: boolean;
  quantity: number;
  holding_aum_pct: number;
  current_value: number;
};

type RedemptionCash = {
  instant_cash: number;
  cash_reserve: number | null;
  available_cash: number;
};

const SellOrderForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SellOrderFormData>({
    scheme_ids: [],
    company_id: 0,
    order_type: formDefaults.sellOrderForm.targetParameters.order_type,
    h1: formDefaults.sellOrderForm.targetParameters.h1,
    h2: formDefaults.sellOrderForm.targetParameters.h2,
    median_volume_pct: formDefaults.sellOrderForm.medianVolume.median_volume_pct,
    condition_1: 0,
    condition_2: 0,
    min_order_val: null,
    sell_method: 1,
    apply_volume_cap: formDefaults.sellOrderForm.medianVolume.apply_volume_cap,
    is_only_long_term: false,
    long_term_months: 12,
    is_redemption: false,
    is_full_redemption: false,
    client_ids: [],
  });

  const [schemes, setSchemes] = useState<Scheme[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const [holdingsData, setHoldingsData] = useState<HoldingRow[]>([]);
  const [holdingsLoading, setHoldingsLoading] = useState(false);
  const [sellPercentages, setSellPercentages] = useState<Record<number, string>>({});
  const [inheritedOnly, setInheritedOnly] = useState(false);
  // 'amount': every holding sells the same % so portfolio weights are unchanged.
  // 'percentage': the user enters a % per holding.
  const [redeemBy, setRedeemBy] = useState<'amount' | 'percentage'>('amount');
  const [redemptionAmount, setRedemptionAmount] = useState('');
  // % of holdings value kept back from cash; a preference, so it survives client changes.
  const [cashBufferPct, setCashBufferPct] = useState(String(formDefaults.sellOrderForm.partialRedemption.cash_buffer_pct));
  // Client cash a redemption pays out first; only the shortfall is sold from stock.
  const [redemptionCash, setRedemptionCash] = useState<RedemptionCash | null>(null);

  useEffect(() => {
    loadSupportingData();
  }, []);

  // Reload companies whenever selected schemes change (empty = all companies)
  useEffect(() => {
    let cancelled = false;
    const config = formData.scheme_ids.length > 0
      ? { params: { scheme_ids: formData.scheme_ids.join(',') } }
      : undefined;
    supportingDataApi.getOrderEligibleCompanies(config).then(data => {
      if (cancelled) return;
      setCompanies(data);
      setFormData(prev => prev.company_id && !data.some((c: Company) => c.id === prev.company_id)
        ? { ...prev, company_id: 0 }
        : prev
      );
    }).catch(err => {
      if (!cancelled) setError('Failed to load companies');
      console.error(err);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(formData.scheme_ids)]);

  // Fetch holdings for partial redemption when a single scheme + single client are selected
  useEffect(() => {
    const isPartialRedemption = formData.is_redemption && !formData.is_full_redemption;
    if (!isPartialRedemption || formData.scheme_ids.length === 0 || formData.client_ids.length === 0) {
      setHoldingsData([]);
      setSellPercentages({});
      setRedemptionAmount('');
      setRedemptionCash(null);
      return;
    }
    // A newer request (client changed, inherited-only toggled) supersedes this one, so a
    // slow earlier response must not overwrite its holdings and cash or reset the amount.
    let cancelled = false;
    const clientId = formData.client_ids[0];
    setHoldingsLoading(true);
    const params: { client_master_id: number; is_inherited?: boolean } = { client_master_id: clientId };
    if (inheritedOnly) params.is_inherited = true;
    ordersApi.getPartialRedemptionHoldings(params)
      .then(({ holdings, instant_cash, cash_reserve, available_cash }) => {
        if (cancelled) return;
        setHoldingsData(holdings);
        setRedemptionCash({ instant_cash, cash_reserve, available_cash });
        const initial: Record<number, string> = {};
        holdings.forEach(row => { initial[row.company_id] = ''; });
        setSellPercentages(initial);
        setRedemptionAmount('');
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Error loading holdings:', err);
        setHoldingsData([]);
        setRedemptionCash(null);
      })
      .finally(() => {
        if (!cancelled) setHoldingsLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.is_redemption, formData.is_full_redemption, JSON.stringify(formData.scheme_ids), JSON.stringify(formData.client_ids), inheritedOnly]);

  // Outside redemption, a client pick is only offered on schemes that allow client-wise orders.
  const clientWiseSchemeIds = useMemo(
    () => formData.scheme_ids.filter(id => schemes.find(s => s.id === id)?.allow_client_wise_orders),
    [formData.scheme_ids, schemes]
  );
  const clientPickerSchemeIds = formData.is_redemption ? formData.scheme_ids : clientWiseSchemeIds;
  const { schemeClients, loading: clientsLoading } = useSchemeClients(clientPickerSchemeIds);

  const loadSupportingData = async () => {
    try {
      setDataLoading(true);
      const schemesData = await supportingDataApi.getSchemes();
      setSchemes(schemesData.filter(s => s.is_active));
    } catch (err) {
      setError('Failed to load form data');
      console.error('Error loading supporting data:', err);
    } finally {
      setDataLoading(false);
    }
  };

  // Partial-redemption values derived from state. Declared before the handlers and
  // validation that read them, so their order cannot depend on when those run.
  const totalExpectedValue = holdingsData.reduce((sum, row) => {
    const pct = parseFloat(sellPercentages[row.company_id] || '0');
    if (isNaN(pct) || row.quantity === 0) return sum;
    return sum + sellValueAtPct(row, pct);
  }, 0);
  const totalHoldingsValue = holdingsData.reduce((sum, row) => sum + row.current_value, 0);
  const availableCash = redemptionCash?.available_cash ?? 0;
  const parsedCashBufferPct = parseFloat(cashBufferPct);
  const cashBufferAmount = parsedCashBufferPct > 0 ? parsedCashBufferPct / 100 * totalHoldingsValue : 0;
  const usableCash = usableRedemptionCash(availableCash, totalHoldingsValue, parsedCashBufferPct);
  // Redeeming by amount spans the whole portfolio, so it is unavailable on the inherited-only subset.
  const isAmountMode = redeemBy === 'amount' && !inheritedOnly;
  const amountModePct = isAmountMode ? parseFloat(sellPercentages[holdingsData[0]?.company_id] || '') : NaN;
  const redemptionSplit = isAmountMode && parseFloat(redemptionAmount) > 0
    ? splitRedemptionAmount(parseFloat(redemptionAmount), usableCash)
    : null;

  // Order-holdings values and the cash ladder come back in the scheme's base currency,
  // and partial redemption is single-scheme, so the first pick decides it.
  const baseCurrency = schemes.find(s => s.id === formData.scheme_ids[0])?.base_currency || CurrencyCode.INR;
  const formatCurrency = (val: number) => formatBaseCurrency(val, baseCurrency, 2);

  const isGlobal = isGlobalSelection(formData.scheme_ids, schemes);
  // Mutual funds and global listings have no traded-volume data to cap against.
  const volumeCapApplies = !isGlobal
    && getSchemeGroupValue(formData.scheme_ids, schemes) !== ORDER_CALC_TYPE_MUTUAL_FUND;
  const { orderConstraints } = formDefaults.sellOrderForm;
  const minOrderVal = formData.min_order_val
    ?? (isGlobal ? orderConstraints.global_min_order_val : orderConstraints.min_order_val);

  const validateForm = () => {
    const errors: Record<string, string> = {};

    const isFullRedemptionVal = formData.is_redemption && formData.is_full_redemption;
    const isPartialRedemptionVal = formData.is_redemption && !formData.is_full_redemption;
    const isRedemptionAny = formData.is_redemption;
    const parsedMedianVolumePct = parseFloat(String(formData.median_volume_pct));
    const parsedMinOrderVal = parseFloat(String(minOrderVal));
    const parsedLongTermMonths = parseFloat(String(formData.long_term_months));

    if (!formData.scheme_ids.length) errors.scheme_ids = 'Scheme is required';
    if (!formData.sell_method) errors.sell_method = 'Sell method is required';

    if (!isFullRedemptionVal && !isPartialRedemptionVal) {
      if (!formData.order_type) errors.order_type = 'Order type is required';
      if (volumeCapApplies && (formData.median_volume_pct === '' || isNaN(parsedMedianVolumePct))) errors.median_volume_pct = 'Median Value Pct is required';
      if (minOrderVal === '' || isNaN(parsedMinOrderVal) || parsedMinOrderVal === 0) errors.min_order_val = 'Min order value is required';
      if (!formData.company_id) errors.company_id = 'Company is required';
      if (formData.h1 === '' || formData.h1 === null || formData.h1 === undefined) errors.h1 = 'H1 is required';
      if (formData.h2 === '' || !formData.h2) errors.h2 = 'H2 is required';
      if (formData.condition_1 === '' || parseFloat(String(formData.condition_1)) === 0) errors.condition_1 = 'Condition 1 is required';
      if (formData.condition_2 === '' || parseFloat(String(formData.condition_2)) === 0) errors.condition_2 = 'Condition 2 is required';
    }

    if (isFullRedemptionVal && volumeCapApplies) {
      if (formData.median_volume_pct === '' || isNaN(parsedMedianVolumePct)) errors.median_volume_pct = 'Median Value Pct is required';
    }

    if (formData.is_only_long_term && (formData.long_term_months === '' || isNaN(parsedLongTermMonths) || parsedLongTermMonths <= 0)) {
      errors.long_term_months = 'Valid long term months is required';
    }

    if (isRedemptionAny) {
      // Each scheme's order goes out with only its own picked clients, and an empty list
      // means every eligible client — so a scheme with no pick would redeem its whole base.
      const schemesWithoutClient = formData.scheme_ids.filter(
        id => clientIdsForScheme(id, formData.client_ids, schemeClients).length === 0,
      );
      if (formData.client_ids.length === 0) {
        errors.client_ids = 'A client must be selected for redemption orders';
      } else if (schemesWithoutClient.length > 0) {
        const codes = schemesWithoutClient
          .map(id => schemes.find(s => s.id === id)?.code ?? `ID ${id}`)
          .join(', ');
        errors.client_ids = `Select at least one client for each scheme. None picked for: ${codes}`;
      }
    }

    if (isPartialRedemptionVal && holdingsData.length > 0 && isAmountMode) {
      const amount = parseFloat(redemptionAmount);
      const bufferPct = parseFloat(cashBufferPct);
      const { fromStocks } = splitRedemptionAmount(amount, usableCash);
      if (cashBufferPct === '' || isNaN(bufferPct) || bufferPct < 0 || bufferPct > 100) {
        errors.cash_buffer_pct = 'Cash buffer must be between 0 and 100%';
      }
      if (redemptionAmount === '') {
        errors.redemption_amount = 'Enter the amount to redeem';
      } else if (isNaN(amount) || amount <= 0) {
        errors.redemption_amount = 'Redemption amount must be greater than 0';
      } else if (fromStocks <= 0) {
        errors.redemption_amount = `Usable cash of ${formatCurrency(usableCash)} (after the buffer) covers this redemption — no sell orders are needed`;
      } else if (proportionalSellPct(holdingsData, fromStocks) === null) {
        errors.redemption_amount = `Redemption amount cannot exceed holdings plus usable cash (${formatCurrency(totalHoldingsValue + usableCash)})`;
      }
    }

    if (isPartialRedemptionVal && holdingsData.length > 0 && !errors.redemption_amount && !errors.cash_buffer_pct) {
      const pctValues = Object.values(sellPercentages).map(v => parseFloat(v));
      const hasAnyPct = pctValues.some(v => v > 0);
      if (!hasAnyPct) {
        errors.sell_percentages = 'Enter a sell percentage for at least one holding';
      } else if (pctValues.some(v => !isNaN(v) && (v < 0 || v > 100))) {
        errors.sell_percentages = 'Sell percentage must be between 0 and 100';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    const numberFields = ['company_id', 'sell_method', 'order_type', 'h1', 'h2', 'median_volume_pct', 'condition_1', 'condition_2', 'min_order_val', 'long_term_months'];
    const booleanYesNoFields = ['apply_volume_cap', 'is_only_long_term', 'is_redemption', 'is_full_redemption'];

    setFormData(prev => {
      const newValue = booleanYesNoFields.includes(name)
        ? value === 'yes'
        : numberFields.includes(name)
          ? (value === '' || value === '-' ? value : parseFloat(value))
          : value;

      const updates: Partial<SellOrderFormData> = { [name]: newValue };

      // Reset client selection and full redemption when switching off redemption mode
      if (name === 'is_redemption' && value === 'no') {
        updates.client_ids = [];
        updates.is_full_redemption = false;
        updates.scheme_ids = [];
        updates.apply_volume_cap = formDefaults.sellOrderForm.medianVolume.apply_volume_cap;
      }

      // Reset client + holdings state when switching between full / partial
      if (name === 'is_full_redemption') {
        updates.client_ids = [];
        updates.scheme_ids = [];
      }

      // Auto-disable volume cap when redemption is enabled
      if ((name === 'is_redemption' || name === 'is_full_redemption') && value === 'yes') {
        updates.apply_volume_cap = false;
      }

      return { ...prev, ...updates };
    });

    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSchemeIdsChange = (newSchemeIds: number[]) => {
    setFormData(prev => {
      // A newly added mutual fund scheme forces the volume cap off.
      const addedMutualFund = newSchemeIds.some(id =>
        !prev.scheme_ids.includes(id) &&
        schemes.find(s => s.id === id)?.order_calc_type === ORDER_CALC_TYPE_MUTUAL_FUND
      );
      return {
        ...prev,
        scheme_ids: newSchemeIds,
        // Clients belong to a scheme, so drop any the new scheme set no longer covers.
        client_ids: pruneClientIds(prev.client_ids, newSchemeIds, schemeClients),
        ...(addedMutualFund ? { apply_volume_cap: false } : {}),
      };
    });
    if (validationErrors.scheme_ids) {
      setValidationErrors(prev => ({ ...prev, scheme_ids: '' }));
    }
  };

  // Single-select scheme handler for partial redemption
  const handlePartialSchemeChange = (selected: SingleValue<SelectOption>) => {
    setFormData(prev => ({ ...prev, scheme_ids: selected ? [selected.value] : [], client_ids: [] }));
    setHoldingsData([]);
    setSellPercentages({});
    setRedemptionAmount('');
    if (validationErrors.scheme_ids) {
      setValidationErrors(prev => ({ ...prev, scheme_ids: '' }));
    }
  };

  const handleClientChange = (selectedIds: number[]) => {
    setFormData(prev => ({ ...prev, client_ids: selectedIds }));
    if (validationErrors.client_ids) {
      setValidationErrors(prev => ({ ...prev, client_ids: '' }));
    }
  };

  // Single-select client handler for partial redemption
  const handlePartialClientChange = (selected: SingleValue<SelectOption>) => {
    setFormData(prev => ({ ...prev, client_ids: selected ? [selected.value] : [] }));
    setHoldingsData([]);
    setSellPercentages({});
    setRedemptionAmount('');
    if (validationErrors.client_ids) {
      setValidationErrors(prev => ({ ...prev, client_ids: '' }));
    }
  };

  const handleSellPercentageChange = (companyId: number, value: string) => {
    setSellPercentages(prev => ({ ...prev, [companyId]: value }));
    if (validationErrors.sell_percentages) {
      setValidationErrors(prev => ({ ...prev, sell_percentages: '' }));
    }
  };

  // Fill every holding with the one % that raises what the usable cash cannot cover.
  const applyRedemptionAmount = (amount: string, bufferPct: string) => {
    const cash = usableRedemptionCash(availableCash, totalHoldingsValue, parseFloat(bufferPct));
    const { fromStocks } = splitRedemptionAmount(parseFloat(amount), cash);
    const pct = amount === '' || !(fromStocks > 0) ? null : proportionalSellPct(holdingsData, fromStocks);
    setSellPercentages(Object.fromEntries(
      holdingsData.map(row => [row.company_id, pct === null ? '' : pct.toFixed(2)])
    ));
    setValidationErrors(prev => ({ ...prev, redemption_amount: '', cash_buffer_pct: '', sell_percentages: '' }));
  };

  const handleRedemptionAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRedemptionAmount(e.target.value);
    applyRedemptionAmount(e.target.value, cashBufferPct);
  };

  const handleCashBufferChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCashBufferPct(e.target.value);
    applyRedemptionAmount(redemptionAmount, e.target.value);
  };

  const handleRedeemByChange = (mode: 'amount' | 'percentage') => {
    setRedeemBy(mode);
    setRedemptionAmount('');
    setSellPercentages(Object.fromEntries(holdingsData.map(row => [row.company_id, ''])));
    setValidationErrors(prev => ({ ...prev, redemption_amount: '', sell_percentages: '' }));
  };

  const handleCompanyChange = (selected: SingleValue<SelectOption>) => {
    setFormData(prev => ({ ...prev, company_id: selected ? selected.value : 0 }));
    if (validationErrors.company_id) {
      setValidationErrors(prev => ({ ...prev, company_id: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { scheme_ids, client_ids, is_redemption, is_full_redemption, company_id, h1, h2, condition_1, condition_2, ...baseOrderData } = formData;

      const toNum = (v: number | string) => parseFloat(String(v)) || 0;
      const parsedBase = {
        ...baseOrderData,
        median_volume_pct: toNum(baseOrderData.median_volume_pct),
        min_order_val: toNum(minOrderVal),
        sell_method: toNum(baseOrderData.sell_method),
        order_type: toNum(baseOrderData.order_type),
        long_term_months: toNum(baseOrderData.long_term_months),
        // Force volume cap to false for all redemption types
        apply_volume_cap: !is_redemption && volumeCapApplies && formData.apply_volume_cap,
      };

      const isPartialRedemption = is_redemption && !is_full_redemption;
      const isFullRedemptionMode = is_redemption && is_full_redemption;

      if (isPartialRedemption) {
        // One order per company with a non-zero sell percentage
        const scheme_id = scheme_ids[0];
        const orderClientIds = client_ids;
        const partialPromises = holdingsData
          .filter(row => parseFloat(sellPercentages[row.company_id] || '0') > 0)
          .map(row => {
            const pct = parseFloat(sellPercentages[row.company_id]);
            return ordersApi.submitSellOrder({
              ...parsedBase,
              scheme_id,
              company_id: row.company_id,
              is_redemption: true,
              is_full_redemption: false,
              order_type: 3, // SELL_PCT_OF_HOLDINGS — also enforced by backend
              h1: 0,
              h2: 100,
              condition_1: pct,
              condition_2: pct,
              min_order_val: 1,
              client_ids: orderClientIds,
            });
          });

        const partialResults = await Promise.allSettled(partialPromises);
        const succeeded = partialResults.filter(r => r.status === 'fulfilled').length;
        const failed = partialResults.filter(r => r.status === 'rejected').length;
        if (failed > 0) {
          setError(`${succeeded} order(s) submitted successfully, ${failed} failed. Please review and resubmit the failed orders.`);
          return;
        }
        navigate('/orders', {
          state: {
            message: `Successfully submitted ${succeeded} partial redemption order(s)`,
            orderSubmitted: true,
            activeTab: 'inprogress',
          },
        });
        return;
      }

      const promises = scheme_ids.map(scheme_id => {
        // Empty means every eligible client in the scheme; only redemption and
        // client-wise schemes narrow it to the clients picked for that scheme.
        const orderClientIds = is_redemption || clientWiseSchemeIds.includes(scheme_id)
          ? clientIdsForScheme(scheme_id, client_ids, schemeClients)
          : [];

        const orderPayload = isFullRedemptionMode
          ? {
            ...parsedBase,
            scheme_id,
            is_redemption: true,
            is_full_redemption: true,
            h1: 0,
            h2: 100,
            condition_1: 100,
            condition_2: 100,
            min_order_val: 1,
            client_ids: orderClientIds,
          }
          : {
            ...parsedBase,
            scheme_id,
            is_redemption,
            is_full_redemption,
            company_id,
            h1: toNum(h1),
            h2: toNum(h2),
            condition_1: toNum(condition_1),
            condition_2: toNum(condition_2),
            client_ids: orderClientIds,
          };

        return ordersApi.submitSellOrder(orderPayload);
      });

      const results = await Promise.all(promises);
      const successCount = results.reduce((acc, r) => acc + (r.count ?? (r.order_id ? 1 : 0)), 0);

      navigate('/orders', {
        state: {
          message: `Successfully submitted ${successCount} sell order(s) for ${formData.scheme_ids.length} scheme(s)`,
          orderSubmitted: true,
          activeTab: 'inprogress'
        }
      });
    } catch (err: unknown) {
      const raw = err as Record<string, unknown>;
      const errData = ((raw.response as { data?: Record<string, unknown> })?.data ?? raw) as {
        detail?: string; error?: string;
      };
      setError(errData.detail || errData.error || 'Failed to submit sell order');
      console.error('Error submitting sell order:', err);
    } finally {
      setLoading(false);
    }
  };

  // Derived flags
  const isFullRedemption = formData.is_redemption && formData.is_full_redemption;
  const isPartialRedemption = formData.is_redemption && !formData.is_full_redemption;
  const showCompany = !isFullRedemption && !isPartialRedemption;
  const showSellParameters = !isFullRedemption && !isPartialRedemption;

  // Derived options for react-select
  const schemeOptions: SelectOption[] = schemes.map(s => ({ value: s.id, label: s.code ?? String(s.id) }));
  const selectedSingleSchemeOption = schemeOptions.find(o => o.value === formData.scheme_ids[0]) ?? null;

  const companyOptions: SelectOption[] = companies.map(c => ({ value: c.id, label: `${c.name} (${c.symbol})` }));
  const selectedCompanyOption = companyOptions.find(o => o.value === formData.company_id) ?? null;

  const partialClientOptions: SelectOption[] = (schemeClients[formData.scheme_ids[0]] || []).map(c => ({
    value: c.id,
    label: `${c.client_code} - ${c.full_name || `${c.first_name} ${c.last_name}`.trim()}`,
  }));
  const selectedPartialClientOption = partialClientOptions.find(o => o.value === formData.client_ids[0]) ?? null;

  const thStyle: React.CSSProperties = {
    padding: '10px 12px', fontWeight: 600, fontSize: '0.82rem',
    color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.04em',
    borderBottom: '2px solid var(--border)',
  };
  const tdStyle: React.CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };
  const statCaptionStyle: React.CSSProperties = {
    margin: '0 0 10px', fontSize: '0.75rem', fontWeight: 600,
    letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-dim)',
  };

  // Tiles round to the whole unit so full amounts fit; the tooltip keeps the decimals.
  const amountTile = (label: string, value: number, hint: string): StatItem => ({
    label,
    value: formatBaseCurrency(value, baseCurrency, 0),
    title: formatCurrency(value),
    hint,
  });
  const cashReserve = redemptionCash?.cash_reserve ?? null;
  const cashStats: StatItem[] = [
    amountTile('Holdings Value', totalHoldingsValue, `${holdingsData.length} holding${holdingsData.length === 1 ? '' : 's'}`),
    amountTile('Instant Cash', redemptionCash?.instant_cash ?? 0, 'Net of block amount & pending trades'),
    cashReserve === null
      ? { label: 'Reserve Cash', value: '—', hint: 'Unavailable — no cash used' }
      : amountTile('Reserve Cash', cashReserve, 'Min cash / fee reserve'),
    amountTile('Cash Buffer', cashBufferAmount, `${parsedCashBufferPct > 0 ? parsedCashBufferPct : 0}% of holdings value`),
    amountTile('Available Cash', usableCash, 'Instant − reserve − buffer'),
  ];
  const splitStats: StatItem[] = redemptionSplit
    ? [
      { ...amountTile('From Cash', redemptionSplit.fromCash, 'Paid out of available cash'), emphasis: true },
      {
        ...amountTile(
          'From Stocks',
          redemptionSplit.fromStocks,
          redemptionSplit.fromStocks <= 0
            ? 'Covered by cash — no sell orders'
            : isNaN(amountModePct)
              ? 'Exceeds holdings value'
              : `Sells ${amountModePct.toFixed(2)}% of each holding`,
        ),
        emphasis: true,
      },
      amountTile('Projected Sale', totalExpectedValue, 'After rounding to whole shares'),
    ]
    : [
      { label: 'From Cash', value: '—', hint: 'Enter an amount to redeem', emphasis: true },
      { label: 'From Stocks', value: '—', hint: 'Enter an amount to redeem', emphasis: true },
      { label: 'Projected Sale', value: '—', hint: 'After rounding to whole shares' },
    ];

  return (
    <FormCard
      title="Create Sell Order"
      subtitle="Generate sell orders, redemptions and partial redemptions."
      backLabel="← Back to Orders"
      onBack={() => navigate('/orders')}
      errorBanner={error ?? undefined}
      submitLabel={loading ? 'Submitting…' : 'Submit Sell Order'}
      onSubmit={handleSubmit}
      onCancel={() => navigate('/orders')}
      submitting={loading}
      loading={dataLoading}
      loadingMessage="Loading form data…"
      variant="page"
    >
      <FormSection title="Client &amp; Scheme Details">
        <FormGrid>
          <FormField label="Is Redemption?">
            <FormSelect
              name="is_redemption"
              value={formData.is_redemption ? 'yes' : 'no'}
              onChange={handleInputChange}
              disabled={loading}
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </FormSelect>
          </FormField>

          {formData.is_redemption && (
            <FormField label="Is Full Redemption?">
              <FormSelect
                name="is_full_redemption"
                value={formData.is_full_redemption ? 'yes' : 'no'}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </FormSelect>
            </FormField>
          )}

          {isPartialRedemption ? (
            <FormField label="Scheme" required error={validationErrors.scheme_ids}>
              <FormReactSelect
                options={schemeOptions}
                value={selectedSingleSchemeOption}
                onChange={handlePartialSchemeChange}
                placeholder="Select scheme…"
                hasError={Boolean(validationErrors.scheme_ids)}
                isDisabled={loading}
              />
            </FormField>
          ) : (
            <OrderSchemeSelector
              schemes={schemes}
              value={formData.scheme_ids}
              onChange={handleSchemeIdsChange}
              error={validationErrors.scheme_ids}
              disabled={loading}
            />
          )}

          {isPartialRedemption && (
            <FormField label="Client" required error={validationErrors.client_ids}>
              {clientsLoading ? (
                <FormFieldNote>Loading clients…</FormFieldNote>
              ) : formData.scheme_ids.length === 0 ? (
                <FormFieldNote>Select a scheme to load clients.</FormFieldNote>
              ) : (
                <FormReactSelect
                  options={partialClientOptions}
                  value={selectedPartialClientOption}
                  onChange={handlePartialClientChange}
                  placeholder="Select client…"
                  hasError={Boolean(validationErrors.client_ids)}
                  isDisabled={loading}
                />
              )}
            </FormField>
          )}

          {(isFullRedemption || (!formData.is_redemption && clientWiseSchemeIds.length > 0)) && (
            <OrderClientSelector
              schemes={schemes}
              schemeIds={clientPickerSchemeIds}
              schemeClients={schemeClients}
              value={formData.client_ids}
              onChange={handleClientChange}
              loading={clientsLoading}
              required={isFullRedemption}
              hint={isFullRedemption
                ? undefined
                : 'Leave empty to order for every eligible client. A scheme with none of its clients selected orders for all of them.'}
              error={validationErrors.client_ids}
              disabled={loading}
            />
          )}

          {showCompany && (
            <FormField
              label="Company"
              required={!formData.is_redemption}
              error={validationErrors.company_id}
            >
              <FormReactSelect
                options={companyOptions}
                value={selectedCompanyOption}
                onChange={handleCompanyChange}
                placeholder="Select company…"
                hasError={Boolean(validationErrors.company_id)}
                isDisabled={loading}
              />
            </FormField>
          )}
        </FormGrid>
      </FormSection>

      {isPartialRedemption && (
        <FormSection title="Holdings">
          {formData.scheme_ids.length > 0 && formData.client_ids.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 32, rowGap: 12, marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.9rem', color: 'var(--text)', fontWeight: 500 }}>Redeem by</span>
                <Button
                  size="sm"
                  variant={isAmountMode ? 'primary' : 'secondary'}
                  aria-pressed={isAmountMode}
                  onClick={() => handleRedeemByChange('amount')}
                  disabled={inheritedOnly}
                  title={inheritedOnly ? 'Amount spans the whole portfolio — unavailable with inherited companies only' : undefined}
                >
                  Amount
                </Button>
                <Button
                  size="sm"
                  variant={!isAmountMode ? 'primary' : 'secondary'}
                  aria-pressed={!isAmountMode}
                  onClick={() => handleRedeemByChange('percentage')}
                >
                  % per holding
                </Button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.9rem', color: 'var(--text)', fontWeight: 500 }}>Inherited companies only</span>
                <Button
                  size="sm"
                  variant={!inheritedOnly ? 'primary' : 'secondary'}
                  aria-pressed={!inheritedOnly}
                  onClick={() => setInheritedOnly(false)}
                >
                  No
                </Button>
                <Button
                  size="sm"
                  variant={inheritedOnly ? 'primary' : 'secondary'}
                  aria-pressed={inheritedOnly}
                  onClick={() => setInheritedOnly(true)}
                >
                  Yes
                </Button>
              </div>
            </div>
          )}
          {holdingsLoading ? (
            <Placeholder kind="loading" message="Loading holdings..." />
          ) : formData.scheme_ids.length === 0 || formData.client_ids.length === 0 ? (
            <Placeholder message="Select a scheme and client to view holdings." />
          ) : holdingsData.length === 0 ? (
            <Placeholder
              message="No holdings found"
              hint="This client has no holdings as of the last working day."
            />
          ) : (
            <>
              {inheritedOnly && redeemBy === 'amount' && (
                <div style={{ marginBottom: 12 }}>
                  <FormFieldNote>
                    Redeeming by amount is spread across the whole portfolio — enter a % per holding, or switch &quot;Inherited companies only&quot; to No.
                  </FormFieldNote>
                </div>
              )}
              {isAmountMode && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginBottom: 24 }}>
                  <div>
                    <h3 style={statCaptionStyle}>Cash available for redemption</h3>
                    <StatGrid compact items={cashStats} />
                  </div>

                  <FormGrid>
                    <FormField
                      label={`Redemption Amount (${baseCurrency})`}
                      required
                      error={validationErrors.redemption_amount}
                      hint="Available cash pays out first; the rest sells the same % of every holding, so portfolio weights stay unchanged."
                    >
                      <FormInput
                        type="number"
                        name="redemption_amount"
                        value={redemptionAmount}
                        onChange={handleRedemptionAmountChange}
                        step="0.01"
                        min="0"
                        max={totalHoldingsValue + usableCash}
                        placeholder="Enter amount to redeem"
                        hasError={Boolean(validationErrors.redemption_amount)}
                        disabled={loading}
                      />
                    </FormField>
                    <FormField
                      label="Cash Buffer (%)"
                      required
                      error={validationErrors.cash_buffer_pct}
                      hint="Share of holdings value kept back from cash."
                    >
                      <FormInput
                        type="number"
                        name="cash_buffer_pct"
                        value={cashBufferPct}
                        onChange={handleCashBufferChange}
                        step="0.01"
                        min="0"
                        max="100"
                        hasError={Boolean(validationErrors.cash_buffer_pct)}
                        disabled={loading}
                      />
                    </FormField>
                  </FormGrid>

                  <div>
                    <h3 style={statCaptionStyle}>Redemption split</h3>
                    <StatGrid compact items={splitStats} />
                  </div>
                </div>
              )}
              {validationErrors.sell_percentages && (
                <span className="field-error">{validationErrors.sell_percentages}</span>
              )}
              <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                <table className={shared.table} style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, position: 'sticky', top: 0, background: 'var(--surface-2)', zIndex: 1, boxShadow: '0 1px 0 var(--border)' }}>Company</th>
                      <th style={{ ...thStyle, textAlign: 'right', position: 'sticky', top: 0, background: 'var(--surface-2)', zIndex: 1, boxShadow: '0 1px 0 var(--border)' }}>Holding AUM (%)</th>
                      <th style={{ ...thStyle, textAlign: 'right', position: 'sticky', top: 0, background: 'var(--surface-2)', zIndex: 1, boxShadow: '0 1px 0 var(--border)' }}>Quantity</th>
                      <th style={{ ...thStyle, textAlign: 'right', position: 'sticky', top: 0, background: 'var(--surface-2)', zIndex: 1, boxShadow: '0 1px 0 var(--border)' }}>Unit Price</th>
                      <th style={{ ...thStyle, textAlign: 'right', position: 'sticky', top: 0, background: 'var(--surface-2)', zIndex: 1, boxShadow: '0 1px 0 var(--border)' }}>Current Value</th>
                      <th style={{ ...thStyle, textAlign: 'right', width: 140, position: 'sticky', top: 0, background: 'var(--surface-2)', zIndex: 1, boxShadow: '0 1px 0 var(--border)' }}>% to Sell</th>
                      <th style={{ ...thStyle, textAlign: 'right', position: 'sticky', top: 0, background: 'var(--surface-2)', zIndex: 1, boxShadow: '0 1px 0 var(--border)' }}>Expected Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {holdingsData.map((row) => {
                      const pct = parseFloat(sellPercentages[row.company_id] || '0');
                      const expectedValue = isNaN(pct) ? 0 : sellValueAtPct(row, pct);
                      return (
                        <tr key={row.company_id} style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface)', transition: 'background 0.15s' }} onMouseEnter={e => (e.currentTarget.style.background = 'var(--row-hover)')} onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface)')}>
                          <td style={tdStyle}>
                            <span style={{ fontWeight: 600, color: 'var(--text)' }}>{row.company_symbol}</span>
                            <span style={{ display: 'block', color: 'var(--text-dim)', fontSize: '0.8rem' }}>{row.company_name}</span>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text)' }}>{row.holding_aum_pct.toFixed(2)}%</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text)' }}>{row.quantity.toLocaleString('en-IN')}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text)' }}>{formatCurrency(row.unit_price)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text)' }}>{formatCurrency(row.current_value)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={sellPercentages[row.company_id] ?? ''}
                                onChange={e => handleSellPercentageChange(row.company_id, e.target.value)}
                                disabled={loading}
                                readOnly={isAmountMode}
                                title={isAmountMode ? 'Set from Redemption Amount — switch Redeem by to % per holding to edit individually' : undefined}
                                style={{
                                  width: 80,
                                  padding: '6px 10px',
                                  border: '1px solid var(--border-strong)',
                                  borderRadius: 6,
                                  fontSize: '0.9rem',
                                  textAlign: 'right',
                                  color: 'var(--text)',
                                  background: loading || isAmountMode ? 'var(--surface-2)' : 'var(--surface)',
                                  outline: 'none',
                                  transition: 'border-color 0.2s, box-shadow 0.2s',
                                }}
                                placeholder="0"
                                onFocus={e => { e.target.style.borderColor = 'var(--accent)'; e.target.style.boxShadow = '0 0 0 3px var(--focus-ring)'; }}
                                onBlur={e => { e.target.style.borderColor = 'var(--border-strong)'; e.target.style.boxShadow = 'none'; }}
                              />
                              <span style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>%</span>
                            </div>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: pct > 0 ? 600 : 400, color: pct > 0 ? 'var(--accent)' : 'var(--text-faint)' }}>
                            {pct > 0 ? formatCurrency(expectedValue) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 1 }}>
                    <tr style={{ background: 'var(--surface-2)', borderTop: '2px solid var(--border)' }}>
                      <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--text)' }} colSpan={6}>Total Expected Value</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: 'var(--accent)' }}>
                        {formatCurrency(totalExpectedValue)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </FormSection>
      )}

      {!isFullRedemption && !isPartialRedemption && (
        <FormSection title="Order Parameters">
          <FormGrid>
            <FormField label="Order Type" required error={validationErrors.order_type}>
              <FormSelect
                name="order_type"
                value={formData.order_type}
                onChange={handleInputChange}
                hasError={Boolean(validationErrors.order_type)}
                disabled={loading}
              >
                <option value={0}>Select Order Type</option>
                <option value={1}>Sell to Target Percentage</option>
                <option value={2}>Sell By Percentage</option>
                <option value={3}>Sell Percentage of Holdings</option>
              </FormSelect>
            </FormField>

            {showSellParameters && (
              <>
                <FormField label="H1 (%)" required error={validationErrors.h1}>
                  <FormInput
                    type="number" name="h1" value={formData.h1} onChange={handleInputChange}
                    step="0.01" min="0" max="100"
                    hasError={Boolean(validationErrors.h1)} disabled={loading}
                  />
                </FormField>

                <FormField label="H2 (%)" required error={validationErrors.h2}>
                  <FormInput
                    type="number" name="h2" value={formData.h2} onChange={handleInputChange}
                    step="0.01" min="0" max="100"
                    hasError={Boolean(validationErrors.h2)} disabled={loading}
                  />
                </FormField>

                <FormField label="Condition 1" required error={validationErrors.condition_1}>
                  <FormInput
                    type="number" name="condition_1" value={formData.condition_1} onChange={handleInputChange}
                    step="0.01" min="0"
                    hasError={Boolean(validationErrors.condition_1)} disabled={loading}
                  />
                </FormField>

                <FormField label="Condition 2" required error={validationErrors.condition_2}>
                  <FormInput
                    type="number" name="condition_2" value={formData.condition_2} onChange={handleInputChange}
                    step="0.01" min="0"
                    hasError={Boolean(validationErrors.condition_2)} disabled={loading}
                  />
                </FormField>
              </>
            )}

            <FeatureFlag flag={FEATURE_FLAGS.ENABLE_LONG_TERM_SELL_ORDER}>
              <FormField label="Is Long Term?">
                <FormSelect
                  name="is_only_long_term"
                  value={formData.is_only_long_term ? 'yes' : 'no'}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </FormSelect>
              </FormField>

              {formData.is_only_long_term && (
                <FormField label="Long Term Months" required error={validationErrors.long_term_months}>
                  <FormInput
                    type="number" name="long_term_months" value={formData.long_term_months}
                    onChange={handleInputChange} min="1"
                    hasError={Boolean(validationErrors.long_term_months)} disabled={loading}
                  />
                </FormField>
              )}
            </FeatureFlag>
          </FormGrid>
        </FormSection>
      )}

      <FormSection title="Order Limits">
        <FormGrid>
          {!isFullRedemption && !isPartialRedemption && (
            <FormField label="Min Order Value" required error={validationErrors.min_order_val}>
              <FormInput
                type="number" name="min_order_val" value={minOrderVal}
                onChange={handleInputChange} step="0.01" min="0"
                hasError={Boolean(validationErrors.min_order_val)} disabled={loading}
              />
            </FormField>
          )}

          <FormField label="Sell Method" required error={validationErrors.sell_method}>
            <FormSelect
              name="sell_method"
              value={formData.sell_method}
              onChange={handleInputChange}
              hasError={Boolean(validationErrors.sell_method)}
              disabled={loading}
            >
              <option value={0}>Select Sell Method</option>
              <option value={1}>VWAP</option>
              <option value={2}>&lt;10% volume</option>
              <option value={3}>Incognito</option>
            </FormSelect>
          </FormField>

          {!isFullRedemption && !isPartialRedemption && volumeCapApplies && (
            <>
              <FormField label="POV Cap (%)" required error={validationErrors.median_volume_pct}>
                <FormInput
                  type="number" name="median_volume_pct" value={formData.median_volume_pct}
                  onChange={handleInputChange} step="0.01" min="0"
                  hasError={Boolean(validationErrors.median_volume_pct)} disabled={loading}
                />
              </FormField>

              <FormField label="Apply Volume Cap" required error={validationErrors.apply_volume_cap}>
                <FormSelect
                  name="apply_volume_cap"
                  value={formData.apply_volume_cap ? 'yes' : 'no'}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </FormSelect>
              </FormField>
            </>
          )}
        </FormGrid>
      </FormSection>
    </FormCard>
  );
};

export default SellOrderForm;
